import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { getUserRoles } from '../../utils/authRoles';
import { getDisplayRoleLabel } from '../../utils/displayRole';
import { canCreateUsersInContext } from '../../utils/userCreationPermissions';
import {
  attachOrganizationToNetwork,
  detachOrganizationFromNetwork,
  getConsultantNetwork,
  getConsultantOrganizations,
  updateConsultantNetwork,
} from './api';
import ContextUserCreateModal from '../users/ContextUserCreateModal';
import SearchableOrganizationSelect from '../networks/SearchableOrganizationSelect';

const emptyAttach = { organization_id: '' };
const emptyNetworkForm = { name: '', description: '' };

const ConsultantNetworkDetail = () => {
  const { id } = useParams();
  const { user } = useUser();
  const isConsultantAdmin = useMemo(() => getUserRoles(user).includes('consultant_admin'), [user]);
  const canCreateNetworkUsers = useMemo(
    () => canCreateUsersInContext(user, { type: 'network', id: Number(id) }),
    [id, user]
  );
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const [isLoading, setIsLoading] = useState(true);
  const [isAttaching, setIsAttaching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [network, setNetwork] = useState(null);
  const [networkForm, setNetworkForm] = useState(emptyNetworkForm);
  const [organizations, setOrganizations] = useState([]);
  const [networkUsers, setNetworkUsers] = useState([]);
  const [consultantOrganizations, setConsultantOrganizations] = useState([]);

  const [attachForm, setAttachForm] = useState(emptyAttach);

  const attachModal = useDisclosure();
  const addUserModal = useDisclosure();

  const loadNetwork = useCallback(async () => {
    setIsLoading(true);
    try {
      const [networkPayload, consultantOrgsPayload] = await Promise.all([
        getConsultantNetwork(id),
        getConsultantOrganizations(),
      ]);

      const networkRecord = networkPayload.network || null;
      setNetwork(networkRecord);
      setNetworkForm({
        name: networkRecord?.name || '',
        description: networkRecord?.description || '',
      });
      setOrganizations(networkPayload.organizations || []);
      setNetworkUsers(networkPayload.users || []);
      setConsultantOrganizations(consultantOrgsPayload || []);
    } catch (error) {
      console.error('Error loading network details:', error);
      toast({ title: 'Could not load network', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    loadNetwork();
  }, [loadNetwork]);

  const attachableOrganizations = useMemo(() => {
    const attachedIds = new Set(organizations.map((organization) => Number(organization.id)));
    return consultantOrganizations
      .filter((organization) => !attachedIds.has(Number(organization.id)))
      .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
  }, [consultantOrganizations, organizations]);

  const handleSaveNetwork = async (event) => {
    event.preventDefault();
    if (!isConsultantAdmin) return;

    setIsSaving(true);
    try {
      await updateConsultantNetwork(id, {
        name: networkForm.name.trim(),
        description: networkForm.description.trim() || null,
      });
      toast({ title: 'Network updated', status: 'success', duration: 3000, isClosable: true });
      await loadNetwork();
    } catch (error) {
      console.error('Error updating network:', error);
      toast({ title: 'Could not update network', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAttachOrganization = async (event) => {
    event.preventDefault();
    setIsAttaching(true);
    try {
      await attachOrganizationToNetwork(id, attachForm.organization_id);
      toast({ title: 'School attached', status: 'success', duration: 3000, isClosable: true });
      setAttachForm(emptyAttach);
      attachModal.onClose();
      await loadNetwork();
    } catch (error) {
      console.error('Error attaching organization:', error);
      toast({ title: 'Could not attach school', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsAttaching(false);
    }
  };

  const handleDetachOrganization = async (organizationId) => {
    const confirmed = window.confirm('Detach this school from the network?');
    if (!confirmed) return;

    try {
      await detachOrganizationFromNetwork(id, organizationId);
      toast({ title: 'School detached', status: 'success', duration: 3000, isClosable: true });
      await loadNetwork();
    } catch (error) {
      console.error('Error detaching organization:', error);
      toast({ title: 'Could not detach school', status: 'error', duration: 4000, isClosable: true });
    }
  };

  if (isLoading) {
    return <HStack justify="center" py={12}><Spinner size="lg" /></HStack>;
  }

  return (
    <Box p={{ base: 4, md: 6 }}>
      <Heading size="lg" mb={1}>{network?.name || 'Network'}</Heading>
      <Text color="gray.500" mb={5}>Manage schools and network users.</Text>

      <VStack align="stretch" spacing={4}>
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="sm" mb={3}>Network details</Heading>
            {isConsultantAdmin ? (
              <form onSubmit={handleSaveNetwork}>
                <VStack spacing={3}>
                  <FormControl isRequired>
                    <FormLabel>Name</FormLabel>
                    <Input
                      value={networkForm.name}
                      onChange={(event) => setNetworkForm((prev) => ({ ...prev, name: event.target.value }))}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Input
                      value={networkForm.description}
                      onChange={(event) => setNetworkForm((prev) => ({ ...prev, description: event.target.value }))}
                    />
                  </FormControl>
                  <HStack width="100%" justify="flex-end">
                    <Button type="submit" colorScheme="blue" variant="outline" isLoading={isSaving}>
                      Save changes
                    </Button>
                  </HStack>
                </VStack>
              </form>
            ) : (
              <VStack align="stretch" spacing={2}>
                <Box>
                  <Text fontSize="sm" color="gray.500">Name</Text>
                  <Text>{network?.name || '—'}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">Description</Text>
                  <Text>{network?.description || '—'}</Text>
                </Box>
              </VStack>
            )}
          </CardBody>
        </Card>

        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <HStack justify="space-between" mb={3}>
              <Heading size="sm">Attached schools</Heading>
              <Button size="sm" variant="outline" onClick={attachModal.onOpen}>Attach school</Button>
            </HStack>

            {organizations.length === 0 ? (
              <Text color="gray.500" fontSize="sm">No schools attached.</Text>
            ) : (
              <TableContainer>
                <Table>
                  <Thead>
                    <Tr>
                      <Th>Name</Th>
                      <Th>City</Th>
                      <Th>Country</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {organizations.map((organization) => (
                      <Tr key={organization.id}>
                        <Td>{organization.name}</Td>
                        <Td>{organization.city || '—'}</Td>
                        <Td>{organization.country || '—'}</Td>
                        <Td>
                          <Button size="sm" variant="outline" colorScheme="red" onClick={() => handleDetachOrganization(organization.id)}>
                            Detach
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </CardBody>
        </Card>

        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <HStack justify="space-between" mb={3}>
              <Heading size="sm">Network users</Heading>
              {canCreateNetworkUsers ? (
                <Button size="sm" variant="outline" onClick={addUserModal.onOpen}>Add network user</Button>
              ) : null}
            </HStack>

            {networkUsers.length === 0 ? (
              <Text color="gray.500" fontSize="sm">No network users yet.</Text>
            ) : (
              <TableContainer>
                <Table>
                  <Thead>
                    <Tr>
                      <Th>Name</Th>
                      <Th>Email</Th>
                      <Th>Role</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {networkUsers.map((networkUser) => (
                      <Tr key={networkUser.id}>
                        <Td>{`${networkUser.first_name || ''} ${networkUser.last_name || ''}`.trim() || networkUser.name || '—'}</Td>
                        <Td>{networkUser.email || '—'}</Td>
                        <Td>{getDisplayRoleLabel(networkUser.context_role || networkUser.role || 'network_viewer')}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </CardBody>
        </Card>
      </VStack>

      <Modal isOpen={attachModal.isOpen} onClose={attachModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Attach school</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleAttachOrganization}>
            <ModalBody>
              <FormControl isRequired>
                <FormLabel>School</FormLabel>
                <SearchableOrganizationSelect
                  options={attachableOrganizations}
                  value={attachForm.organization_id}
                  onChange={(organizationId) => setAttachForm({ organization_id: organizationId })}
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={attachModal.onClose}>Cancel</Button>
              <Button type="submit" colorScheme="blue" isLoading={isAttaching} isDisabled={!attachForm.organization_id}>
                Attach
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      <ContextUserCreateModal
        isOpen={addUserModal.isOpen}
        onClose={addUserModal.onClose}
        context={{ type: 'network', id: Number(id), namespace: 'consultant' }}
        title="Add network user"
        submitLabel="Add user"
        onCreated={loadNetwork}
      />
    </Box>
  );
};

export default ConsultantNetworkDetail;
